import { useState, useContext, useEffect } from 'react'
import { IntakeContext } from '../context/IntakeContext'
import {
  Card,
  Tooltip,
  Fade,
  IconButton,
  Box,
  Grid,
  Typography,
} from '@mui/material'

import {
  format,
  subYears,
  differenceInYears,
  addYears,
  addMonths,
  max,
  min,
} from 'date-fns'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { useFieldFocus } from 'context/FieldFocusContext'
import { BeachAccess, Help } from '@mui/icons-material'
import { WheelSelector } from './WheelSelector'

function getMinimumRetirementAge(birthYear) {
  if (birthYear < 1948) return { years: 55, months: 0 }
  if (birthYear === 1948) return { years: 55, months: 2 }
  if (birthYear === 1949) return { years: 55, months: 4 }
  if (birthYear === 1950) return { years: 55, months: 6 }
  if (birthYear === 1951) return { years: 55, months: 8 }
  if (birthYear === 1952) return { years: 55, months: 10 }
  if (birthYear >= 1953 && birthYear <= 1964) return { years: 56, months: 0 }
  if (birthYear === 1965) return { years: 56, months: 2 }
  if (birthYear === 1966) return { years: 56, months: 4 }
  if (birthYear === 1967) return { years: 56, months: 6 }
  if (birthYear === 1968) return { years: 56, months: 8 }
  if (birthYear === 1969) return { years: 56, months: 10 }
  return { years: 57, months: 0 }
}
function addRetirementAge(date, { years, months }) {
  let result = addYears(date, years)
  result = addMonths(result, months)
  return result
}

const OnboardingServiceCompDate = ({ onboarding }) => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const [showAgeAtSoonestFullyEligibleDate, setShowAgeAtSoonestFullyEligibleDate] = useState(onboarding ? false : true)
  const dateToYears = intakeState.dateOfHire
    ? differenceInYears(new Date(), new Date(intakeState.dateOfHire))
    : ''
  const yearsToDate = (years) =>
    format(subYears(new Date(), years), 'yyyy-MM-dd')

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600)
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const todaysDate = new Date()
  const birthYear = new Date(intakeState?.dateOfBirth).getFullYear()
  const minimumRetirementAgeYearsMonths = getMinimumRetirementAge(birthYear)
  const dateOfHireResolver = intakeState?.dateOfHire
    ? intakeState.dateOfHire
    : '1990-01-01'

  const eligibilityScenariosFirstFullyEligibleDate = min([
    max([
      addYears(new Date(dateOfHireResolver), 30),
      addRetirementAge(
        new Date(intakeState?.dateOfBirth),
        minimumRetirementAgeYearsMonths
      ),
    ]),
    max([
      addYears(new Date(dateOfHireResolver), 30),
      addYears(new Date(intakeState?.dateOfBirth), 57),
    ]),
    max([
      addYears(new Date(dateOfHireResolver), 20),
      addYears(new Date(intakeState?.dateOfBirth), 60),
    ]),
    max([
      addYears(new Date(dateOfHireResolver), 5),
      addYears(new Date(intakeState?.dateOfBirth), 62),
    ]),
  ])
  const eligibilityScenariosFirstFullyEligibleDateSpecialFERS = min([
    max([
      addYears(new Date(dateOfHireResolver), 20),
      addYears(new Date(intakeState.dateOfBirth), 50),
    ]),
    addYears(new Date(dateOfHireResolver), 25),
  ])
  const eligibilityScenariosFirstFullyEligibleDateFSPS = min([
    max([
      addYears(new Date(dateOfHireResolver), 20),
      addYears(new Date(intakeState.dateOfBirth), 50),
    ]),
    max([
      addYears(new Date(dateOfHireResolver), 5),
      addYears(new Date(intakeState.dateOfBirth), 62),
    ]),
  ])
  const eligibilityScenariosFirstFullyEligibleDateCSRS = min([
    addYears(new Date(intakeState.dateOfBirth), 55),
  ])
  // const eligibilityScenariosFirstFullyEligibleDateCSRSFrozen = min([
  //   max([
  //     addYears(new Date(dateOfHireResolver), 30),
  //     addYears(new Date(intakeState.dateOfBirth), minimumRetirementAge),
  //   ]),
  // ])

  // check for all retiement systems with fers being the fallback
  const firstFullyEligibleDate =
    intakeState?.retirementSystem === 'special_fers_(leo,_atc_etc.)'
      ? eligibilityScenariosFirstFullyEligibleDateSpecialFERS
      : intakeState?.retirementSystem ===
        'foreign_service_officer_pension_(fsps)'
      ? eligibilityScenariosFirstFullyEligibleDateFSPS
      : intakeState?.retirementSystem === 'csrs'
      ? eligibilityScenariosFirstFullyEligibleDateCSRS
      : eligibilityScenariosFirstFullyEligibleDate
  const soonestFullyEligibleDate = new Date(
    firstFullyEligibleDate.getFullYear(),
    firstFullyEligibleDate.getMonth() + 1,
    0
  )
  const soonestFullyEligibleFormattedDate =
    soonestFullyEligibleDate < new Date()
      ? "You're Eligible to Retire Now!"
      : format(soonestFullyEligibleDate, 'yyyy')
  const soonestFullyEligibleFormattedDateToStore = format(
    soonestFullyEligibleDate < new Date()
      ? new Date(todaysDate.getFullYear(), todaysDate.getMonth() + 1, 0)
      : soonestFullyEligibleDate,
    'yyyy-MM-dd'
  )
  const ageAtSoonestFullyEligibleDate = intakeState?.dateOfBirth
    ? differenceInYears(
        new Date(soonestFullyEligibleFormattedDateToStore),
        new Date(intakeState.dateOfBirth)
      )
    : ''

  const fieldId = 'compDateHelp'
  const newHelpText = `Your official date of hire with the government, including any bought back military time. If you don't know, put your best guess or check your latest SF-50.`
  const fieldId2 = 'fraHelp'
  const newHelpText2 = `The date you can recieve an Unreduced Pension, Full Access to your TSP, and Lifetime access to Federal Healthcare.`

  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  useEffect(() => {
    registerHelpField(fieldId, newHelpText)
    registerHelpField(fieldId2, newHelpText2)
  }, [fieldId])

  useEffect(() => {
    updateIntakeStateAtKey({
      dateOfPlannedRetirement: soonestFullyEligibleFormattedDateToStore,
      dateOfPlannedRetirementYearsBased: true,
    })
  }, [updateIntakeStateAtKey, soonestFullyEligibleFormattedDateToStore])

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Box
        onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
        onMouseLeave={() => handleBlur()}
      >
        <Box mb={1}>
          <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
            How many years of service do you have?
          </Typography>
        </Box>
        {/* <Typography
          variant="body1"
          sx={{ lineHeight: '1.2' }}
          fontSize={{ xs: '16px', sm: '20px' }}
        >
          This helps us calculate your earliest retirement date
        </Typography> */}
        <Grid container columnSpacing={3} mt={4}>
          <Grid item xs={12} mb={4}>
            {isMobile ? (
              <Grid item xs={12}>
                <WheelSelector
                  options={Array.from({ length: 51 }, (_, i) => i)}
                  initialValue={dateToYears}
                  onChange={(val) => {
                    updateIntakeStateAtKey({
                      dateOfHire: yearsToDate(val),
                      dateOfPlannedRetirement:
                        soonestFullyEligibleFormattedDateToStore,
                    })
                  }}
                  visibleCount={20}
                  optionItemHeight={40}
                  width={300}
                  formatter={(val) => `${val}`}
                  suffixText="years"
                  suffixGap={8}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                {renderMuiFormField({
                  showAdornment: true,
                  required: true,
                  inputType: 'int',
                  id: 'dateOfBirth',
                  label: 'Years of Service',
                  value: dateToYears,
                  enableHelpText: true,
                  onChange: (e) => {
                    updateIntakeStateAtKey({
                      dateOfHire: yearsToDate(e),
                      dateOfPlannedRetirement:
                        soonestFullyEligibleFormattedDateToStore,
                    })
                  },
                })}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
      {showAgeAtSoonestFullyEligibleDate && <>
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{ backgroundColor: '#F0F4F8' }}
        display={dateOfHireResolver === '1990-01-01' ? 'none' : 'block'}
        onMouseEnter={(e) => handleFocus(fieldId2, e.currentTarget)}
        onMouseLeave={() => handleBlur()}
      >
        <Card
          sx={{
            textAlign: 'center',
            boxShadow: 1,
            borderRadius: 2,
            padding: { xs: '1rem', md: '2rem' },
          }}
        >
          <Typography variant="h4" fontSize={{ xs: '16px', sm: '20px' }}>
            Your First Fully Eligible Age
            {isMobile && (
              <Tooltip
                TransitionComponent={Fade}
                title={
                  'The age you can recieve an Unreduced Pension, Full Access to your TSP, and Lifetime access to Federal Healthcare'
                }
                placement="top"
                color="info"
                enterTouchDelay={0}
                leaveTouchDelay={3000}
              >
                <IconButton size="small" color="dark">
                  <Help sx={{ marginTop: '-10px' }} />
                </IconButton>
              </Tooltip>
            )}
          </Typography>
          <Typography
            variant="h3"
            fontWeight="bold"
            fontSize={{ xs: '18px', md: '30px' }}
            mt={1}
            color="#344767"
          >
            Age {ageAtSoonestFullyEligibleDate} (
            {soonestFullyEligibleFormattedDate}
            )
            <BeachAccess
              sx={{
                marginLeft: '10px',
                marginBottom: '-3px',
              }}
            />
          </Typography>
        </Card>
        {/* <Typography variant="body2" mt={2} sx={{ lineHeight: '1.3' }}>
                Did you find this helpful? Create an account to see loads of other
                useful information to help you plan for your retirment.
              </Typography> */}
      </Box>
      </>}
    </Box>
  )
}
export default OnboardingServiceCompDate
